import { useEffect, useState, useMemo } from "react";
import { TrendingUp, ArrowUp, ArrowDown, Cpu, Zap, Radio } from "lucide-react";

// Fun bot-personality strings that rotate every few seconds — pure client-side theatre
// so the user "sees" the bot working during their 7-day cycle.
const BOT_ACTIVITIES = [
  "Scanning BTC/USDT order book…",
  "Detecting arbitrage between ETH/USDT and ETH/USDC…",
  "Confirming signal on 15m RSI divergence…",
  "Placing limit order at book depth…",
  "Rebalancing exposure to reduce drawdown…",
  "Filled: SOL/USDT LONG · profit locked",
  "Filled: BNB/USDT SHORT · closing hedge",
  "Analyzing on-chain flows for whale movement…",
  "Backtesting last-24h pattern against 90d history…",
  "Adjusting stop-loss to trail 0.8%…",
  "Waiting for volatility spike on ETH…",
  "Cross-checking Binance / OKX spread…",
];

function useTicker(intervalMs, cb) {
  useEffect(() => {
    const iv = setInterval(cb, intervalMs);
    return () => clearInterval(iv);
  }, [intervalMs, cb]);
}

export default function BotWorkingAnimation({ botId, invested, botName, pairSuggestion = "BTC/USDT" }) {
  const [activityIdx, setActivityIdx] = useState(0);
  const [pulse, setPulse] = useState(0);
  const [ticks, setTicks] = useState(() => Array.from({ length: 24 }, () => 0.4 + Math.random() * 0.6));

  useTicker(2800, () => setActivityIdx((i) => (i + 1) % BOT_ACTIVITIES.length));
  useTicker(180, () => setPulse((p) => (p + 1) % 100));
  useTicker(700, () => setTicks((t) => {
    const nxt = t.slice(1);
    nxt.push(0.35 + Math.random() * 0.6);
    return nxt;
  }));

  const activity = BOT_ACTIVITIES[activityIdx];
  // Random-but-stable-per-render trade decisions for the fake feed
  const feed = useMemo(() => {
    const pairs = ["BTC/USDT","ETH/USDT","SOL/USDT","BNB/USDT","XRP/USDT","AVAX/USDT","LINK/USDT"];
    return Array.from({ length: 4 }).map((_, i) => {
      const p = pairs[(activityIdx + i) % pairs.length];
      const side = (activityIdx + i) % 2 === 0 ? "LONG" : "SHORT";
      const pnl = ((Math.sin(pulse / 5 + i) + 1) * 0.05 + i * 0.01).toFixed(4);
      return { id: `${p}-${i}`, pair: p, side, pnl };
    });
  }, [activityIdx, pulse]);

  return (
    <div className="mt-4 rounded-xl border border-[#8AE234]/20 bg-gradient-to-br from-[#0A1626] via-[#070B14] to-[#0A1626] p-4 overflow-hidden relative" data-testid="bot-working-animation">
      {/* Ambient scan-line */}
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div
          className="absolute inset-x-0 h-24"
          style={{
            background: `linear-gradient(180deg, transparent, rgba(138,226,52,0.09), transparent)`,
            top: `${(pulse * 2) % 200 - 20}%`,
            transition: "top 0.18s linear",
          }}
        />
      </div>

      <div className="relative grid md:grid-cols-3 gap-4">
        {/* Left: bot avatar + status */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-14 w-14 rounded-2xl border border-[#8AE234]/40 bg-[#8AE234]/10 flex items-center justify-center">
              <Cpu size={22} className="text-[#8AE234]"/>
            </div>
            <div className="absolute -top-1 -right-1 h-3 w-3">
              <div className="absolute inset-0 rounded-full bg-[#8AE234] animate-ping"/>
              <div className="absolute inset-0.5 rounded-full bg-[#8AE234]"/>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8AE234] font-mono flex items-center gap-1">
              <Radio size={10}/> Live · Trading
            </div>
            <div className="font-heading text-white text-lg leading-tight">{botName || `Bot-${botId}`}</div>
            <div className="text-[11px] font-mono text-slate-400 mt-0.5">Deployed: ${Number(invested || 0).toFixed(2)} USDT</div>
          </div>
        </div>

        {/* Middle: real-time waveform */}
        <div className="flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1"><Zap size={10}/> Market Pulse</div>
          <div className="flex items-end gap-0.5 h-12" data-testid="bot-pulse-wave">
            {ticks.map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm"
                style={{
                  height: `${v * 100}%`,
                  background: `linear-gradient(180deg, #8AE234, rgba(138,226,52,0.15))`,
                  transition: "height 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: i === ticks.length - 1 ? "0 0 8px rgba(138,226,52,0.6)" : "none",
                }}
              />
            ))}
          </div>
          <div className="mt-1 font-mono text-[10px] text-slate-400 truncate" data-testid="bot-activity-log" key={activityIdx}>
            <span className="text-[#8AE234]">▸</span> {activity}
          </div>
        </div>

        {/* Right: simulated trade feed */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1"><TrendingUp size={10}/> Latest Signals</div>
          <div className="space-y-1">
            {feed.map((f) => (
              <div key={f.id} className="flex items-center justify-between text-[11px] font-mono rounded border border-white/5 bg-[#070B14] px-2 py-1">
                <div className="flex items-center gap-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${f.side === "LONG" ? "bg-[#8AE234]/15 text-[#8AE234]" : "bg-red-500/15 text-red-400"}`}>{f.side}</span>
                  <span className="text-slate-300">{f.pair}</span>
                </div>
                <span className="text-[#8AE234] flex items-center gap-0.5">
                  {f.side === "LONG" ? <ArrowUp size={9}/> : <ArrowDown size={9}/>}+${f.pnl}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
